<template>
  <BaseCard :heading="$t('customers.tags.heading')">
    <div :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]">
      <BaseText>{{ $t('customers.tags.description') }}</BaseText>
      <BaseText
        :href="helpCenterArticleUrl($t('customers.tags.help_article_url'))"
      >
        {{ $t('global.learn_more') }}
      </BaseText>
      <BaseSpinner
        v-if="loading"
        inline
      />
      <div
        v-else
        :class="$style.tags"
      >
        <BaseCard
          v-for="(tag, index) in modelValue"
          :key="index"
          gray
          :closable="!tag.salonized"
          @close="removeTag(index)"
        >
          <BaseInput
            v-model="tag.name"
            :disabled="tag.salonized"
            :label="$t('customers.tags.tag_name')"
            :info="tag.salonized ? $t('customers.tags.undeletable') : undefined"
            v-test="'tag'"
          />
        </BaseCard>
      </div>
      <BaseText
        v-if="modelValue.length < 6"
        iconBefore="plus"
        @click="addTag"
        v-test="'tag-add'"
      >
        {{ $t('customers.tags.create_tag') }}
      </BaseText>
      <BaseAlert
        color="info"
        :text="$t('customers.tags.description_warning')"
      />
    </div>
  </BaseCard>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { GET_CUSTOMER_TAGS } from './graphql';

type Tag = {
  id: number | null;
  name: string;
  salonized?: boolean;
};

const props = defineProps<{
  modelValue: Tag[];
}>();

const emit = defineEmits(['update:modelValue']);

const { onResult, loading } = useQuery(GET_CUSTOMER_TAGS, {
  fetchPolicy: 'cache-first',
});

onResult(({ data }) => {
  if (data.customerTags) {
    updateTags([
      ...data.customerTags.salonizedTags,
      ...data.customerTags.companyTags,
    ]);
  }
});

const addTag = () => {
  updateTags([
    ...props.modelValue,
    {
      id: null,
      name: '',
    },
  ]);
};

const removeTag = (index: number) => {
  updateTags(props.modelValue.filter((_tag, tagIndex) => tagIndex !== index));
};

const updateTags = (value: Tag[]) => {
  emit('update:modelValue', value);
};
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.tags {
  margin-bottom: $spacing;
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;

  .base:not(.smallScreen) & {
    & > * {
      width: calc(50% - #{$spacing * 0.5});
    }
  }

  .base.smallScreen & {
    & > * {
      width: 100%;
    }
  }
}
</style>
